import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

export default function Invite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid invite link'); setLoading(false); return; }
    api.get(`/auth/invite/${token}`)
      .then(res => { setInvite(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || 'Invalid invite link'); setLoading(false); });
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/auth/invite/${token}/accept`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
      });
      // Auto-login
      localStorage.setItem('lf_token', res.data.token);
      localStorage.setItem('lf_user', JSON.stringify(res.data.user));
      localStorage.setItem('lf_tenant', JSON.stringify(res.data.tenant));
      useAuthStore.setState({
        token: res.data.token,
        user: res.data.user,
        tenant: res.data.tenant,
      });
      toast.success(`Welcome to ${invite.firm_name}!`);
      navigate('/');
    } catch(err) {
      toast.error(err.response?.data?.error || 'Failed to accept invite');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500 text-sm">Loading invite...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <p className="text-gray-800 font-medium">{error}</p>
        <p className="text-gray-500 text-sm mt-1">Please contact your administrator for a new invite.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-br from-purple-600 to-purple-800 px-6 py-8 text-center">
          <h1 className="text-white text-xl font-bold">{invite.firm_name}</h1>
          <p className="text-purple-200 text-sm mt-1">
            {invite.invited_by} invited you to join
          </p>
          {invite.role_name && (
            <span className="inline-block mt-2 bg-white/20 text-white text-xs px-3 py-1 rounded-full">
              Role: {invite.role_name}
            </span>
          )}
        </div>
        <form onSubmit={handleAccept} className="px-6 py-6 space-y-4">
          <p className="text-sm text-gray-600 text-center">Set up your account to get started</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First name</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} required
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                placeholder="Jane" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Last name</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} required
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                placeholder="Smith" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input value={invite.email} disabled
              className="w-full border border-gray-100 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
              placeholder="Min 8 characters" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Confirm password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
              placeholder="Repeat password" />
          </div>
          <button type="submit" disabled={submitting || !firstName || !lastName || !password || !confirmPassword}
            className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 transition-colors">
            {submitting ? 'Setting up account...' : 'Accept Invitation & Join'}
          </button>
        </form>
        <p className="text-center text-xs text-gray-400 pb-4">
          Invite expires {new Date(invite.expires_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
